import React, { useEffect, useState } from "react";
import { TextArea } from "semantic-ui-react";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import Button from "../../input/Button";
import { getBulkDropdownData } from "../../../api/keyvalue";
import {
  checkDemmurrage,
  getConfirmDetails,
  getNonDisputeFlagCount,
  jobConfirmation,
  jobFlagsByJobId,
} from "../../../api/jobApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingSpinner from "../../LoadingSpinner";
import { useSelector } from "react-redux";
import { statusByContext } from "../../../api/statusApi";
import { FORMS, MODULES, STATUS_GROUPS } from "../../../const";
import useUserCompany from "../../../hooks/useUserCompany";
import { errorMessageToast } from "../../../helper";
import { demmurrageDropdown } from "../../../api/demmurrageReasonAPI";

const form = FORMS.JOB.CONFIMATION.form;
const JobConfirmation = ({
  jobId,
  reqId,
  jobCode,
  stakeholder,
  errors,
  close,
  resolvedStatus,
}) => {
  const userCompany = useUserCompany();
  const userCurrency = userCompany ? userCompany.comCurrency : null;

  const [activeOptions, setActiveOptions] = useState([]);
  const [statusDropdownOptions, setStatusDropdownOptions] = useState([]);
  const [disputeTypeOptions, setDisputeTypeOption] = useState([]);
  const [confirmOptions, setConfirmOptions] = useState([]);
  const [isDisputed, setIsDisputed] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    action: 1,
    active: 1,
    dispute: "",
    remark: "",
    jobDemmurrageReason: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector((state) => state.user);
  const [editMode, setEditMode] = useState(false);
  const [jobCompleteStatus, setJobCompleteStatus] = useState(null);
  const [jobDisputeStatus, setJobDisputeStatus] = useState(null);
  const [haveNonDisputeFlags, setHaveNonDisputeFlags] = useState(false);
  const [currentErrors, setCurrentErrors] = useState(errors);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [confirmationReasonDetails, setConfirmationReasonDetails] = useState({
    totalJobDuration: "",
    reqPlannedKm: "",
    jobChargeableKm: "",
    isExceeded: false,
    resolvedRemark: "",
  });
  const [reasonOptions, setReasonOptions] = useState([]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        const reasonDetails = await getConfirmDetails(jobId, stakeholder);
        const reasonDropdownOpt = await demmurrageDropdown();
        setReasonOptions(reasonDropdownOpt);

        if (reasonDetails) {
          setConfirmationReasonDetails({
            totalJobDuration: reasonDetails.totalJobDuration || "",
            reqPlannedKm: reasonDetails.reqPlannedKm || "",
            jobChargeableKm: reasonDetails.jobChargeableKm || "",
            isExceeded: !!reasonDetails.isExceeded,
            resolvedRemark: reasonDetails.resolvedRemark || "",
          });
        }
      } catch (e) {
        console.error(e);
      }

      const {
        active,
        "flag_type#4": disputeTypeOptions,
        job_conf_dispute: confirmOptions,
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "flag_type", group: "4" },
        { key: "job_conf_dispute" },
      ]);
      const jobStatusData = await statusByContext(
        STATUS_GROUPS.JOB_CONFIRMATION,
      );

      const jobCompleteStatus = await statusByContext(
        STATUS_GROUPS.JOB_COMPLETED,
      );
      setJobCompleteStatus(jobCompleteStatus ? jobCompleteStatus[0].key : null);
      const jobDisputeStatus = await statusByContext(
        STATUS_GROUPS.JOB_DISPUTED,
      );
      setJobDisputeStatus(jobDisputeStatus ? jobDisputeStatus[0].key : null);

      const nonDisputeFlagCount = await getNonDisputeFlagCount(jobId);
      if (nonDisputeFlagCount === -1) {
        toast.error(`Error occurred while fetching active flag count`, {
          autoClose: 2000,
          containerId: "Modal",
          hideProgressBar: false,
        });

        setTimeout(() => {
          close();
        }, 2000);
        return;
      }
      setHaveNonDisputeFlags(nonDisputeFlagCount > 0);

      if (resolvedStatus === 1) {
        setIsDisputed(false);
      } else {
        setIsDisputed(false);
      }

      setStatusDropdownOptions(jobStatusData);
      setActiveOptions(active);
      setDisputeTypeOption(disputeTypeOptions);
      setConfirmOptions(confirmOptions);
      setIsLoading(false);
    })();
  }, []);

  const handleProceed = async () => {
    if (
      confirmationReasonDetails.isExceeded &&
      !confirmationData.jobDemmurrageReason
    ) {
      toast.error("Reason is required when job duration time is exceeded.", {
        autoClose: 5000,
        containerId: "Modal",
      });
      return;
    }

    if (confirmationData.action === 1) {
      await handleConfirm();
    } else if (confirmationData.action === 2) {
      if (!confirmationData.dispute || !confirmationData.remark) {
        toast.error("Both Dispute Type and Remark are required.", {
          autoClose: 5000,
          containerId: "Modal",
        });
        return;
      }
      await handleDispute();
    }
  };

  const handleConfirm = async () => {
    if (haveNonDisputeFlags) {
      toast.warn("Please resolve all the flags before confirming job.", {
        autoClose: 5000,
        containerId: "Modal",
      });
      return;
    }
    await handleAction("JBC");
  };
  const handleDispute = async () => {
    try {
      await handleAction("JDI");

      toast.success("Dispute flag added successfully!", {
        autoClose: 5000,
        containerId: "Modal",
      });
      setTimeout(() => {
        close();
      }, 2000);
    } catch (error) {
      toast.error("Error while creating dispute flag.", {
        autoClose: 5000,
        containerId: "Modal",
      });
    }
  };

  const handleAction = async (code) => {
    try {
      setIsLoading(true);
      const message = await jobConfirmation({
        job: jobId,
        request: reqId,
        code: code,
        username: user.username,
        active: confirmationData.active,
        dispute: confirmationData.dispute,
        remark: confirmationData.remark,
        currency: userCurrency,
        stakeholder,
        jobDemmurrageReason: confirmationData.jobDemmurrageReason,
      });

      toast.success(message, {
        hideProgressBar: false,
        autoClose: 2000,
        containerId: "Modal",
      });

      setTimeout(() => {
        close();
      }, 2000);
    } catch (error) {
      errorMessageToast(error, 5000, "Modal");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <form
          className="ui form"
          style={{ overflowY: "auto", height: "60vh", padding: "10px" }}
        >
          <div className="ui segment">
            <h4 className="ui dividing header">Job Confirmation</h4>
            <div className="ui segment">
              <div className="field">
                <Input label="Job Code" value={jobCode} disabled={true} />
              </div>
              <div className="three fields">
                <Input
                  label="Total Hours"
                  value={confirmationReasonDetails.totalJobDuration}
                  disabled={true}
                />
                <Input
                  label="Chargeable KM"
                  value={
                    confirmationReasonDetails.jobChargeableKm
                      ? `${confirmationReasonDetails.jobChargeableKm} KM`
                      : ""
                  }
                  disabled={true}
                />
                <Input
                  label="Planned KM (One Way)"
                  value={
                    confirmationReasonDetails.reqPlannedKm
                      ? `${confirmationReasonDetails.reqPlannedKm} KM`
                      : ""
                  }
                  disabled={true}
                />
              </div>
              {confirmationReasonDetails.resolvedRemark && (
                <div className="field">
                  <Input
                    label="Resolve Remark"
                    value={confirmationReasonDetails.resolvedRemark}
                    disabled={true}
                  />
                </div>
              )}

              <div className="field">
                <Dropdown
                  label="Action"
                  placeholder="Select Action"
                  options={confirmOptions}
                  value={confirmationData.action}
                  onChange={(e, { value }) =>
                    setConfirmationData((prev) => ({ ...prev, action: value }))
                  }
                  disabled={isDisputed || resolvedStatus === 1}
                />
              </div>

              {confirmationReasonDetails.isExceeded && (
                // <SearchDropdown
                //     key={1}
                //     editMode={editMode}
                //     field="job_demmurrage_reason"
                //     reset={searchFieldReset}
                //     setReset={setSearchFieldReset}
                //     clearable={true}
                //     onClear={() => {
                //       setConfirmationData((prevState) => ({
                //         ...prevState,
                //         jobDemmurrageReason: "",
                //       }));
                //     }}
                //     onResultSelect={(data) =>
                //         setConfirmationData((prevState) => ({
                //           ...prevState,
                //           jobDemmurrageReason: data,
                //         }))
                //     }
                //     api={demmurrageReasonStartWith}
                //     fieldInfo={{
                //       tableName: "masterdata.tms_m_demmurrage_reason",
                //       primaryKey: "dmr_id",
                //       fieldName: "dmr_reason",
                //       id: confirmationData.jobDemmurrageReason,
                //     }}
                //
                //     label="Reason *"
                //     error={currentErrors.jobDemmurrageReason}
                //     resetError={() =>
                //         setCurrentErrors((prevState) => {
                //           const { jobDemmurrageReason: _, ...rest } = prevState;
                //           return rest;
                //         })
                //     }
                // />
                <Dropdown
                  label="Reason *"
                  placeholder="Reason *"
                  editMode={editMode}
                  field="job_demmurrage_reason"
                  options={reasonOptions}
                  value={confirmationData.jobDemmurrageReason}
                  onChange={(e, { value }) =>
                    setConfirmationData((prevState) => ({
                      ...prevState,
                      jobDemmurrageReason: value,
                    }))
                  }
                />
              )}

              {confirmationData.action === 2 && (
                <>
                  <div className="two fields">
                    <Dropdown
                      label="Active"
                      placeholder="Select Active"
                      editMode={editMode}
                      module={MODULES.Transaction}
                      form={form}
                      field="jcon_active"
                      authenticated={true}
                      needDeletePermission={true}
                      options={activeOptions}
                      value={confirmationData.active}
                      onChange={(e, { value }) =>
                        setConfirmationData((prev) => ({
                          ...prev,
                          active: value,
                        }))
                      }
                    />

                    <Dropdown
                      label="Dispute Type *"
                      placeholder="Select Dispute Type"
                      options={disputeTypeOptions}
                      value={confirmationData.dispute}
                      onChange={(e, { value }) =>
                        setConfirmationData((prev) => ({
                          ...prev,
                          dispute: value,
                        }))
                      }
                    />
                  </div>

                  <div className="field">
                    <label>Remark *</label>
                    <TextArea
                      placeholder="Enter remarks here"
                      value={confirmationData.remark}
                      onChange={(e, { value }) =>
                        setConfirmationData((prev) => ({
                          ...prev,
                          remark: value,
                        }))
                      }
                    />
                  </div>
                </>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <Button
                  template="proceed"
                  onClick={handleProceed}
                  label="Proceed"
                  className="ui green button"
                />
              </div>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default JobConfirmation;
